/**
 * Form Schema Admin Page
 *
 * Manages tenant-specific form schema overrides.
 * Base schemas come from subgraphs (immutable), but tenants can:
 * - Hide fields
 * - Make fields read-only
 * - Override field labels
 * - Add custom fields
 * - Add custom validations
 *
 * ARCHITECTURE:
 * ```
 * Base Schema (from subgraph)  →  immutable, versioned
 *            +
 * Tenant Override (DynamoDB)   →  per-tenant customization
 *            =
 * Merged Schema (runtime)      →  what frontend receives
 * ```
 *
 * CONSTRAINTS:
 * - Repository pattern via generated GraphQL hooks
 * - Data-driven: all form config from backend
 * - Life-critical safety levels enforced
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  Select,
  Card,
  Badge,
  Button,
  Table,
  ActionIcon,
  Tooltip,
  Alert,
  Loader,
  Modal,
  MultiSelect,
  TextInput,
  JsonInput,
  Tabs,
  Code,
  Divider,
  Switch,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconSettings,
  IconEye,
  IconEyeOff,
  IconLock,
  IconTag,
  IconPlus,
  IconDeviceFloppy,
  IconRefresh,
  IconAlertTriangle,
  IconShieldCheck,
  IconCheck,
  IconBrain,
  IconSparkles,
} from '@tabler/icons-react';
import { useTenantId, useAuth } from '@/auth';
import {
  useFormSchema,
  useFormSchemas,
  useFormSchemaOverride,
  useSaveFormSchemaOverride,
} from '@/components/UniversalEntityForm/useFormSchema';
import type {
  FormSchema,
  FormSchemaOverride,
  FormFieldDefinition,
  FormSafetyLevel,
} from '@/generated/graphql';
import { FormSchemaLlmPanel, FormSchemaOverrideDiffPanel, SchemaImportWizard } from '@ferp/workflow-forms';
import { SAFETY_LEVEL_UI_CONFIG, SafetyLevel } from '@/config/safety-levels';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Available subgraphs that can have form schemas
 * NOTE: In production, this would come from SubgraphRegistry
 */
const AVAILABLE_SUBGRAPHS = [
  { value: 'core', label: 'FERP Core (25 Primitives)' },
  { value: 'fhir', label: 'Healthcare (FHIR)' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'erp', label: 'Accounting (ERP)' },
  { value: 'edi', label: 'EDI' },
  { value: 'wearables', label: 'Wearables' },
];

function getSafetyConfig(level: string): { color: string; label: string } {
  const config = SAFETY_LEVEL_UI_CONFIG[level as SafetyLevel];
  return config ? { color: config.color, label: config.label } : { color: 'gray', label: 'Standard' };
}

// =============================================================================
// TYPES
// =============================================================================

interface OverrideFormValues {
  hiddenFields: string[];
  readonlyFields: string[];
  labelOverrides: string; // JSON string
}

// =============================================================================
// SUBCOMPONENTS
// =============================================================================

interface SchemaListProps {
  schemas: FormSchema[];
  selectedEntityType: string | null;
  onSelect: (entityType: string) => void;
  loading: boolean;
}

function SchemaList({ schemas, selectedEntityType, onSelect, loading }: SchemaListProps) {
  if (loading) {
    return (
      <Paper p="xl" withBorder>
        <Group justify="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading schemas...</Text>
        </Group>
      </Paper>
    );
  }

  if (schemas.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Text ta="center" c="dimmed">
          No form schemas found for this subgraph.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="xs">
      {schemas.map((schema) => {
        const safetyConfig = getSafetyConfig(schema.safetyLevel);
        const isSelected = schema.entityType === selectedEntityType;

        return (
          <Card
            key={schema.entityType}
            withBorder
            padding="sm"
            style={{
              cursor: 'pointer',
              borderColor: isSelected ? 'var(--mantine-color-blue-5)' : undefined,
              backgroundColor: isSelected ? 'var(--mantine-color-blue-light)' : undefined,
            }}
            onClick={() => onSelect(schema.entityType)}
          >
            <Group justify="space-between">
              <div>
                <Text size="sm" fw={500}>
                  {schema.displayName}
                </Text>
                <Text size="xs" c="dimmed">
                  {schema.entityType} v{schema.version}
                </Text>
              </div>
              <Group gap="xs">
                <Badge size="xs" variant="light" color={safetyConfig.color}>
                  {safetyConfig.label}
                </Badge>
                {schema.fields && (
                  <Badge size="xs" variant="outline">
                    {schema.fields.length} fields
                  </Badge>
                )}
              </Group>
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
}

interface OverrideEditorProps {
  schema: FormSchema;
  override: FormSchemaOverride | null;
  onSave: (values: OverrideFormValues) => Promise<void>;
  saving: boolean;
}

function OverrideEditor({ schema, override, onSave, saving }: OverrideEditorProps) {
  const form = useForm<OverrideFormValues>({
    initialValues: {
      hiddenFields: override?.hiddenFields ?? [],
      readonlyFields: override?.readonlyFields ?? [],
      labelOverrides: override?.labelOverrides ?? '{}',
    },
  });

  // Build field options from schema
  const fieldOptions = useMemo(
    () =>
      (schema.fields ?? []).map((field) => ({
        value: field.name,
        label: `${field.label ?? field.name} (${field.fieldType})`,
      })),
    [schema.fields]
  );

  const handleSubmit = form.onSubmit(async (values) => {
    // Validate JSON
    try {
      JSON.parse(values.labelOverrides);
    } catch {
      form.setFieldError('labelOverrides', 'Invalid JSON format');
      return;
    }
    await onSave(values);
  });

  const safetyConfig = getSafetyConfig(schema.safetyLevel);

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="lg">
        {/* Schema Info Header */}
        <Paper p="md" withBorder bg="gray.0">
          <Group justify="space-between">
            <div>
              <Group gap="xs">
                <Text fw={600}>{schema.displayName}</Text>
                <Badge size="sm" color={safetyConfig.color}>
                  {safetyConfig.label}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {schema.description ?? `Form schema for ${schema.entityType}`}
              </Text>
            </div>
            <Code>v{schema.version}</Code>
          </Group>
        </Paper>

        {/* Life-Critical Warning */}
        {(schema.safetyLevel === 'CRITICAL' || schema.safetyLevel === 'LIFE_CRITICAL') && (
          <Alert
            color={schema.safetyLevel === 'LIFE_CRITICAL' ? 'red' : 'orange'}
            icon={<IconShieldCheck size={16} />}
          >
            <Text size="sm" fw={500}>
              {schema.safetyLevel === 'LIFE_CRITICAL' ? 'Life-Critical Form' : 'Critical Form'}
            </Text>
            <Text size="xs">
              Changes to this form override will be audited. Exercise caution when hiding or
              making fields read-only as it may affect compliance requirements.
            </Text>
          </Alert>
        )}

        <Tabs defaultValue="visibility">
          <Tabs.List>
            <Tabs.Tab value="visibility" leftSection={<IconEye size={14} />}>
              Visibility
            </Tabs.Tab>
            <Tabs.Tab value="labels" leftSection={<IconTag size={14} />}>
              Labels
            </Tabs.Tab>
            <Tabs.Tab value="preview" leftSection={<IconSettings size={14} />}>
              Preview
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="visibility" pt="md">
            <Stack gap="md">
              {/* Hidden Fields */}
              <div>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color="gray">
                    <IconEyeOff size={12} />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>
                    Hidden Fields
                  </Text>
                </Group>
                <Text size="xs" c="dimmed" mb="xs">
                  These fields will not appear on the form for this tenant.
                </Text>
                <MultiSelect
                  data={fieldOptions}
                  placeholder="Select fields to hide"
                  searchable
                  clearable
                  {...form.getInputProps('hiddenFields')}
                />
              </div>

              {/* Read-only Fields */}
              <div>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color="blue">
                    <IconLock size={12} />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>
                    Read-only Fields
                  </Text>
                </Group>
                <Text size="xs" c="dimmed" mb="xs">
                  These fields will be visible but cannot be edited by users.
                </Text>
                <MultiSelect
                  data={fieldOptions}
                  placeholder="Select fields to make read-only"
                  searchable
                  clearable
                  {...form.getInputProps('readonlyFields')}
                />
              </div>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="labels" pt="md">
            <Stack gap="md">
              <div>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color="violet">
                    <IconTag size={12} />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>
                    Label Overrides
                  </Text>
                </Group>
                <Text size="xs" c="dimmed" mb="xs">
                  Override field labels for this tenant. Format: {`{"fieldName": "New Label"}`}
                </Text>
                <JsonInput
                  placeholder='{"firstName": "Given Name", "lastName": "Family Name"}'
                  formatOnBlur
                  autosize
                  minRows={4}
                  {...form.getInputProps('labelOverrides')}
                />
              </div>

              {/* Quick Label Editor */}
              <Divider label="Quick Edit" labelPosition="center" />
              <Text size="xs" c="dimmed">
                Current field labels (click to add override):
              </Text>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Field</Table.Th>
                    <Table.Th>Default Label</Table.Th>
                    <Table.Th>Override</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(schema.fields ?? []).slice(0, 10).map((field) => {
                    let overrides: Record<string, string> = {};
                    try {
                      overrides = JSON.parse(form.values.labelOverrides);
                    } catch {
                      // Invalid JSON, ignore
                    }
                    const hasOverride = field.name in overrides;

                    return (
                      <Table.Tr key={field.name}>
                        <Table.Td>
                          <Text size="xs" ff="monospace">{field.name}</Text>
                        </Table.Td>
                        <Table.Td>{field.label ?? field.name}</Table.Td>
                        <Table.Td>
                          {hasOverride ? (
                            <Badge size="xs" variant="light" color="violet">
                              {overrides[field.name]}
                            </Badge>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
              {(schema.fields?.length ?? 0) > 10 && (
                <Text size="xs" c="dimmed" ta="center">
                  + {(schema.fields?.length ?? 0) - 10} more fields
                </Text>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="preview" pt="md">
            <Stack gap="md">
              <Alert color="blue" icon={<IconSettings size={16} />}>
                <Text size="sm">
                  Preview shows how the form will appear with current overrides applied.
                </Text>
              </Alert>

              <Paper p="md" withBorder>
                <Text size="sm" fw={500} mb="md">
                  Visible Fields ({(schema.fields?.length ?? 0) - form.values.hiddenFields.length})
                </Text>
                <Stack gap="xs">
                  {(schema.fields ?? [])
                    .filter((f) => !form.values.hiddenFields.includes(f.name))
                    .map((field) => {
                      const isReadonly = form.values.readonlyFields.includes(field.name);
                      let labelOverrides: Record<string, string> = {};
                      try {
                        labelOverrides = JSON.parse(form.values.labelOverrides);
                      } catch {
                        // Invalid JSON
                      }
                      const displayLabel = labelOverrides[field.name] ?? field.label ?? field.name;

                      return (
                        <Group key={field.name} gap="xs">
                          <Text size="sm" style={{ minWidth: 150 }}>
                            {displayLabel}
                            {field.required && (
                              <Text component="span" c="red" size="xs">
                                {' '}
                                *
                              </Text>
                            )}
                          </Text>
                          <Badge size="xs" variant="outline">
                            {field.fieldType}
                          </Badge>
                          {isReadonly && (
                            <Badge size="xs" color="blue" variant="light">
                              Read-only
                            </Badge>
                          )}
                          {labelOverrides[field.name] && (
                            <Badge size="xs" color="violet" variant="light">
                              Custom Label
                            </Badge>
                          )}
                        </Group>
                      );
                    })}
                </Stack>
              </Paper>

              {form.values.hiddenFields.length > 0 && (
                <Paper p="md" withBorder bg="gray.0">
                  <Text size="sm" fw={500} mb="md" c="dimmed">
                    Hidden Fields ({form.values.hiddenFields.length})
                  </Text>
                  <Group gap="xs">
                    {form.values.hiddenFields.map((fieldName) => (
                      <Badge key={fieldName} variant="outline" color="gray">
                        <IconEyeOff size={10} style={{ marginRight: 4 }} />
                        {fieldName}
                      </Badge>
                    ))}
                  </Group>
                </Paper>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Divider />

        {/* Actions */}
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => form.reset()}
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            type="submit"
            leftSection={<IconDeviceFloppy size={16} />}
            loading={saving}
          >
            Save Override
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function FormSchemaAdminPage() {
  const tenantId = useTenantId();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [selectedSubgraph, setSelectedSubgraph] = useState<string | null>(null);
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('override');
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Query schemas for selected subgraph
  const {
    schemas,
    loading: schemasLoading,
    refetch: refetchSchemas,
  } = useFormSchemas({
    subgraph: selectedSubgraph ?? '',
    skip: !selectedSubgraph,
  });

  // Query full schema for selected entity
  const {
    schema: fullSchema,
    loading: schemaLoading,
  } = useFormSchema({
    subgraph: selectedSubgraph ?? '',
    entityType: selectedEntityType ?? '',
    skip: !selectedSubgraph || !selectedEntityType,
  });

  // Query current override
  const {
    override,
    loading: overrideLoading,
    refetch: refetchOverride,
  } = useFormSchemaOverride({
    subgraph: selectedSubgraph ?? '',
    entityType: selectedEntityType ?? '',
    skip: !selectedSubgraph || !selectedEntityType,
  });

  // Save override mutation
  const { saveOverride, loading: saving } = useSaveFormSchemaOverride();

  const handleSaveOverride = useCallback(
    async (values: OverrideFormValues) => {
      if (!tenantId || !selectedSubgraph || !selectedEntityType) return;

      try {
        await saveOverride({
          tenantId,
          subgraph: selectedSubgraph,
          entityType: selectedEntityType,
          hiddenFields: values.hiddenFields,
          readonlyFields: values.readonlyFields,
          labelOverrides: values.labelOverrides,
          customFields: null,
          customValidations: null,
        });

        notifications.show({
          title: 'Override Saved',
          message: `Form override for ${selectedEntityType} has been saved.`,
          color: 'green',
          icon: <IconCheck size={16} />,
        });

        refetchOverride();
      } catch (error) {
        notifications.show({
          title: 'Save Failed',
          message: error instanceof Error ? error.message : 'Failed to save override',
          color: 'red',
          icon: <IconAlertTriangle size={16} />,
        });
      }
    },
    [tenantId, selectedSubgraph, selectedEntityType, saveOverride, refetchOverride]
  );

  if (!tenantId) {
    return (
      <Container>
        <Alert color="red" title="Authentication Required">
          Please log in to manage form schemas.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Form Schema Management</Title>
            <Text c="dimmed" size="sm">
              Customize form schemas for your tenant without modifying base definitions
            </Text>
          </div>
          <Button
            variant="light"
            leftSection={<IconSparkles size={14} />}
            onClick={() => setImportModalOpen(true)}
          >
            AI Import
          </Button>
        </Group>

        {/* AI Schema Import Modal */}
        <Modal
          opened={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          title="AI Schema Import"
          size="lg"
        >
          <SchemaImportWizard
            onComplete={() => {
              setImportModalOpen(false);
              refetchSchemas();
            }}
            onCancel={() => setImportModalOpen(false)}
          />
        </Modal>

        {/* Architecture Info */}
        <Alert color="blue" icon={<IconSettings size={16} />}>
          <Text size="sm" fw={500}>Data-Driven Form Architecture</Text>
          <Text size="xs" mt={4}>
            Base schemas are defined by subgraphs and versioned. Your tenant can customize
            visibility, labels, and add custom fields through overrides. The merged result is
            what users see.
          </Text>
        </Alert>

        {/* Subgraph Selector */}
        <Paper p="md" withBorder>
          <Group>
            <Select
              label="Select Subgraph"
              placeholder="Choose a subgraph"
              data={AVAILABLE_SUBGRAPHS}
              value={selectedSubgraph}
              onChange={(value) => {
                setSelectedSubgraph(value);
                setSelectedEntityType(null);
              }}
              style={{ minWidth: 300 }}
            />
            {selectedSubgraph && (
              <Button
                variant="subtle"
                leftSection={<IconRefresh size={14} />}
                onClick={() => refetchSchemas()}
                mt={24}
              >
                Refresh
              </Button>
            )}
          </Group>
        </Paper>

        {/* Main Content */}
        {selectedSubgraph && (
          <Group ta="left" gap="lg" style={{ flexWrap: 'nowrap' }}>
            {/* Schema List (left panel) */}
            <Paper p="md" withBorder style={{ minWidth: 300, maxWidth: 350 }}>
              <Text size="sm" fw={500} mb="md">
                Entity Types
              </Text>
              <SchemaList
                schemas={schemas}
                selectedEntityType={selectedEntityType}
                onSelect={setSelectedEntityType}
                loading={schemasLoading}
              />
            </Paper>

            {/* Editor Panel (right side) */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              {!selectedEntityType ? (
                <Stack ta="center" py="xl">
                  <IconSettings size={48} color="var(--mantine-color-gray-5)" />
                  <Text c="dimmed">Select an entity type to configure its form</Text>
                </Stack>
              ) : schemaLoading || overrideLoading ? (
                <Group justify="center" py="xl">
                  <Loader />
                </Group>
              ) : fullSchema ? (
                <>
                  {/* Mode Tabs */}
                  <Tabs value={activeTab} onChange={(v) => setActiveTab(v || 'override')} mb="md">
                    <Tabs.List>
                      <Tabs.Tab value="override" leftSection={<IconSettings size={14} />}>
                        Manual Override
                      </Tabs.Tab>
                      <Tabs.Tab value="ai" leftSection={<IconBrain size={14} />}>
                        <Group gap={4}>
                          AI Edit
                          <Badge size="xs" variant="light" color="violet">
                            <IconSparkles size={10} />
                          </Badge>
                        </Group>
                      </Tabs.Tab>
                      <Tabs.Tab value="diff" leftSection={<IconEye size={14} />}>
                        Override Inspector
                      </Tabs.Tab>
                    </Tabs.List>
                  </Tabs>

                  {/* Manual Override Editor */}
                  {activeTab === 'override' && (
                    <OverrideEditor
                      schema={fullSchema}
                      override={override}
                      onSave={handleSaveOverride}
                      saving={saving}
                    />
                  )}

                  {/* AI-Powered Editor */}
                  {activeTab === 'ai' && tenantId && userId && (
                    <FormSchemaLlmPanel
                      entityType={selectedEntityType}
                      subgraph={selectedSubgraph ?? ''}
                      tenantId={tenantId}
                      userId={userId}
                      mode="CREATE"
                      onSchemaChange={(json) => {
                        notifications.show({
                          title: 'Schema Updated',
                          message: 'AI changes applied. Review and save when ready.',
                          color: 'blue',
                        });
                        refetchOverride();
                      }}
                    />
                  )}

                  {/* Override Inspector */}
                  {activeTab === 'diff' && fullSchema && (
                    <FormSchemaOverrideDiffPanel
                      schema={fullSchema}
                      override={override}
                    />
                  )}
                </>
              ) : (
                <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                  Could not load schema for {selectedEntityType}
                </Alert>
              )}
            </Paper>
          </Group>
        )}
      </Stack>
    </Container>
  );
}

export default FormSchemaAdminPage;
